import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { KAMPFKUNST_FEATURES } from '../../assets/data/kampfkunst-features';
import { KAMPFKUNST_PRINZIPIEN } from '../../assets/data/kampfkunst-prinzipien';
import ResponsiveCardDeck from '../../components/card-deck/ResponsiveCardDeck';
import { Card, CardType } from '../../components/card-deck/type';
import CardDialog from '../../components/dialog/card/CardDialog';
import Section from '../../components/section/Section';

const getCard = (type: CardType, id: string) => {
  const items = type === CardType.Feature ? KAMPFKUNST_FEATURES : KAMPFKUNST_PRINZIPIEN;
  return items.find(item => item.id === id);
};

const getHashCard = (type: CardType, hash: string) => {
  const currentHash = hash.substring(1);
  const [hashCardType, hashCardId] = currentHash.split('/');
  return hashCardType === type && hashCardId ? getCard(type, hashCardId) : undefined;
};

type CardHandlerProps = {
  children: ReactNode;
  type: CardType;
};

const CardHandler: FC<CardHandlerProps> = ({ children, type }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const hashCard = getHashCard(type, location.hash);
  const [card, setCard] = useState<Card | undefined>(hashCard);

  const handleCardClick = useCallback((id: string) => setCard(getCard(type, id)), [type]);

  const handleDialogClose = useCallback(() => {
    setCard(undefined);
    navigate({ hash: '' }, { replace: true });
  }, [navigate]);

  useEffect(() => {
    card?.id && navigate({ hash: `${type}/${card.id}` }, { replace: true });
  }, [card?.id, navigate, type]);

  useEffect(() => {
    const hashCard = getHashCard(type, location.hash);
    setCard(prev => (prev && hashCard && prev.id === hashCard.id ? prev : hashCard));
  }, [location.hash, type]);

  return (
    <Section>
      {children}

      <ResponsiveCardDeck
        cards={type === CardType.Feature ? KAMPFKUNST_FEATURES : KAMPFKUNST_PRINZIPIEN}
        onCardClick={handleCardClick}
        sx={{ marginTop: '1.5em' }}
      ></ResponsiveCardDeck>
      {card && <CardDialog card={card} onClose={handleDialogClose} />}
    </Section>
  );
};

export default CardHandler;
